import { activePortalSettings } from '../config/portal-regions';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { signIn, signOut, getCurrentUser, signUp, confirmSignUp, confirmSignIn, type AuthUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import '../config/aws-config'; // Initialize AWS config
import apiService from '../services/api';
import { maintenanceConfig } from '../config/maintenance';
import { queryClient } from '../lib/queryClient';
import { completeSignupLegalConfirmation } from '../services/signupLegalAcceptance';

interface User {
  id: string;
  email: string;
  communicationEmail?: string | null;
  loginIdentifier?: string | null;
  name: string;
  accountId: string | null;
  workspaceId: string;
  role: string;
  isCompanyProfileOwner: boolean;
  permissions: string[];
  workspaces: { workspaceId: string; name: string; workspaceType: 'live' | 'sandbox'; role: string; roleName?: string; isOwner: boolean }[];
  settings: {
    profile: {
      name: string;
      pictureUrl?: string;
      company: string;
      timezone: string;
      defaultSendingDomain: string;
    };
    security: {
      twoFactorAuth: boolean;
      ipWhitelist: string;
      webhookSecret: string;
    };
    notifications: {
      emailAlerts: boolean;
      bounceAlerts: boolean;
      spamAlerts: boolean;
      deliveryAlerts: boolean;
    };
  };
  subscription: {
    id: string;
    status: string;
    planName: string;
    provider?: 'azure' | 'shopify' | 'stripe' | 'google_cloud_marketplace' | 'aws_marketplace' | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    addOns: Record<string, number>;
    scheduledCancelAt?: number;
    scheduledChangeAt?: number;
    pendingSince?: number;
    paymentFailed?: boolean;
    failedInvoiceUrl?: string;
    adminSubscriptionOverride?: {
      status: 'paid' | 'free' | 'none';
      mode?: 'fallback' | 'override';
      appliedAt?: string;
      appliedBy?: string;
    };
  };
  subscriptionAccess?: {
    active: boolean;
    paid: boolean;
  };
  suspended?: boolean;
  suspensionReason?: string;
  pauseSending?: boolean;
  pauseSendingReason?: string;
  pauseSendingAt?: string;
  preferredUseCase?: 'transactional' | 'marketing';
  isFederated?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string, mfaCode?: string, newPassword?: string) => Promise<{ requiresMfa: boolean, requiresEmailConfirmation: boolean, requiresNewPassword: boolean, requiresPasswordReset?: boolean } | void>;
  loginWithPasskey: (email: string) => Promise<{
    requiresMfa: boolean;
    requiresEmailConfirmation: boolean;
    requiresNewPassword: boolean;
    requiresPasswordReset?: boolean;
    success?: boolean;
    passkeyAvailable?: boolean;
    cancelled?: boolean;
  } | void>;
  loginWithSSO: (organizationId: string) => Promise<void>;
  loginWithMicrosoft: (providerName?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithSocialProvider: (providerName: string, providerLabel: string) => Promise<void>;
  signup: (email: string, password: string, name: string, referralCode?: string) => Promise<boolean>;
  confirmSignup: (email: string, code: string) => Promise<{ userId: string }>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  authReady: boolean;
  error: string | null;
  mfaRequired: boolean;
  newPasswordRequired: boolean;
  passwordResetRequired: boolean;
  passwordResetEmail: string;
  setMfaRequired: (value: boolean) => void;
  setNewPasswordRequired: (value: boolean) => void;
  setPasswordResetRequired: (value: boolean) => void;
  clearError: () => void;

  resetMfa: () => void;
  refreshUser: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success' || event.query.queryKey[0] !== 'userSettings') return;
    const workspaceId = event.query.queryKey[1];
    const settings = event.query.state.data as { subscription?: User['subscription']; subscriptionAccess?: User['subscriptionAccess'] } | undefined;
    if (!settings?.subscription) return;
    setUser(current => current !== null && current.workspaceId === workspaceId
      && (JSON.stringify(current.subscription) !== JSON.stringify(settings.subscription) || JSON.stringify(current.subscriptionAccess) !== JSON.stringify(settings.subscriptionAccess))
      ? { ...current, subscription: settings.subscription!, subscriptionAccess: settings.subscriptionAccess }
      : current);
  }), []);

  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const [passwordResetEmail, setPasswordResetEmail] = useState('');
  const [switchingWorkspace, setSwitchingWorkspace] = useState(false);
  const workspaceSwitchRef = useRef<Promise<void> | null>(null);

  const mapAuthUserToUser = async (authUser: AuthUser, allowWorkspaceFallback = true): Promise<User> => {
    apiService.setAuthenticatedUser(authUser.userId);
    let dbUserData;
    try {
      dbUserData = await apiService.getUserSettings();
    } catch (requestError) {
      const statusCode = (requestError as { statusCode?: number })?.statusCode;
      if (!allowWorkspaceFallback || statusCode !== 403 || !apiService.getWorkspaceId()) throw requestError;
      // A stored selection can become invalid after removal from a workspace.
      // Retry without it so the server can choose the deterministic active default.
      apiService.setWorkspaceId(null);
      dbUserData = await apiService.getUserSettings();
    }

    const workspaceId = dbUserData.workspace?.workspaceId || authUser.userId;
    const role = dbUserData.role || 'owner';
    const permissions = dbUserData.permissions || [];

    // Pin API calls to the resolved workspace.
    apiService.setWorkspaceId(workspaceId);

    // The workspace switcher is helpful but must not prevent an otherwise
    // authorized user from signing in when its independent request fails.
    let workspaces: User['workspaces'];
    try {
      const teamsResponse = await apiService.getTeams();
      workspaces = (teamsResponse?.workspaces || []).map(w => ({
        workspaceId: w.workspaceId,
        name: w.name,
        workspaceType: w.workspaceType === 'sandbox' ? 'sandbox' : 'live',
        role: w.role,
        roleName: w.roleName,
        isOwner: w.isOwner,
      }));
    } catch (teamsError) {
      console.warn('Failed to load workspace switcher memberships during sign-in', teamsError);
      workspaces = [{
        workspaceId,
        name: dbUserData.workspace?.name || 'Workspace',
        workspaceType: dbUserData.workspace?.workspaceType === 'sandbox' ? 'sandbox' : 'live',
        role,
        isOwner: role === 'owner',
      }];
    }

    // Federated provider email is persisted by post-confirmation in the same
    // user.email field used by native Cognito sign-ins.
    const resolvedEmail = (
      authUser.signInDetails?.loginId ||
      dbUserData.email ||
      ''
    ).toLowerCase();

    try {
      await completeSignupLegalConfirmation(authUser.userId, resolvedEmail, workspaceId, role);
    } catch (legalError) {
      // Missing or failed evidence leaves the normal owner review available.
      console.warn('Signup legal confirmation could not be recorded', legalError);
    }

    const resolvedName =
      dbUserData.profile?.name ||
      authUser.signInDetails?.loginId?.split('@')[0] ||
      resolvedEmail.split('@')[0] ||
      'User';

    const settings = {
      security: dbUserData.security || {
        twoFactorAuth: false,
        ipWhitelist: '',
        webhookSecret: ''
      },
      notifications: dbUserData.notifications || {
        emailAlerts: true,
        bounceAlerts: true,
        spamAlerts: true,
        deliveryAlerts: false
      },
      profile: dbUserData.profile || {
        name: resolvedName,
        pictureUrl: '',
        company: '',
        timezone: '',
        defaultSendingDomain: ''
      },
      workspaceName: dbUserData.workspace?.name || '',
    };

    // Populate React Query cache with the settings data
    // This ensures useUserSettings hook will have cached data available
    queryClient.setQueryData(['userSettings', workspaceId], {
      accountId: dbUserData.accountId,
      createdAt: dbUserData.createdAt || null,
      email: resolvedEmail,
      communicationEmail: dbUserData.communicationEmail || null,
      emailStatus: dbUserData.emailStatus || 'active',
      loginIdentifier: dbUserData.loginIdentifier || null,
      settings,
      subscription: dbUserData.workspace?.subscription || {
        id: '',
        status: '',
        planName: '',
        currentPeriodStart: '',
        currentPeriodEnd: '',
        addOns: {},
      },
      subscriptionAccess: dbUserData.workspace?.subscriptionAccess,
      suspended: dbUserData.workspace?.suspended || false,
      suspensionReason: dbUserData.workspace?.suspensionReason,
      pauseSending: dbUserData.workspace?.pauseSending || false,
      pauseSendingReason: dbUserData.workspace?.pauseSendingReason,
      pauseSendingAt: dbUserData.workspace?.pauseSendingAt,
      status: dbUserData.status,
      deletionScheduledFor: dbUserData.deletionScheduledFor,
      totalEmailsSent: dbUserData.workspace?.totalEmailsSent || 0,
      hasSentEmail: dbUserData.workspace?.hasSentEmail || false,
      workspaceName: dbUserData.workspace?.name,
      configurations: dbUserData.configurations,
      workspace: dbUserData.workspace,
      role,
      permissions,
      workspaces,
    });

    return {
      id: authUser.userId,
      email: resolvedEmail,
      communicationEmail: dbUserData.communicationEmail || null,
      loginIdentifier: dbUserData.loginIdentifier || null,
      name: resolvedName,
      accountId: dbUserData.accountId,
      workspaceId,
      role,
      isCompanyProfileOwner: dbUserData.workspace?.isCompanyProfileOwner === true,
      permissions,
      workspaces,
      settings,
      subscription: dbUserData.workspace?.subscription,
      subscriptionAccess: dbUserData.workspace?.subscriptionAccess,
      suspended: dbUserData.workspace?.suspended || false,
      suspensionReason: dbUserData.workspace?.suspensionReason,
      pauseSending: dbUserData.workspace?.pauseSending || false,
      pauseSendingReason: dbUserData.workspace?.pauseSendingReason,
      pauseSendingAt: dbUserData.workspace?.pauseSendingAt,
      preferredUseCase: dbUserData.preferredUseCase,
      isFederated: !authUser.signInDetails,
    };
  };

  const checkAuthState = async () => {
    try {
      const authUser = await getCurrentUser();
      const userData = await mapAuthUserToUser(authUser);
      setUser(userData);
    } catch {
      // No authenticated user
    } finally {
      setLoading(false);
      setAuthReady(true);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          void checkAuthState();
          break;
        case 'signInWithRedirect_failure':
          setError('An error occurred during Single Sign-On.');
          setLoading(false);
          setAuthReady(true);
          break;
      }
    });

    const handleWorkspaceStorageChange = (event: StorageEvent) => {
      if (event.key !== 'simplysend:workspace-deleted' || !event.newValue) {
        return;
      }

      try {
        const { workspaceId } = JSON.parse(event.newValue);
        if (workspaceId === apiService.getWorkspaceId()) {
          apiService.setWorkspaceId(null);
          queryClient.clear();
          void checkAuthState();
        }
      } catch {
        // Ignore malformed cross-tab workspace-deletion notifications.
      }
    };

    const handleAuthenticatedUserChange = () => {
      setLoading(true);
      setAuthReady(false);
      queryClient.clear();
      void checkAuthState();
    };

    window.addEventListener('storage', handleWorkspaceStorageChange);
    window.addEventListener('simplysend:auth-user-changed', handleAuthenticatedUserChange);

    // Defer so setState from checkAuthState is not synchronous in the effect body
    queueMicrotask(() => {
      if (!cancelled) {
        void checkAuthState();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('storage', handleWorkspaceStorageChange);
      window.removeEventListener('simplysend:auth-user-changed', handleAuthenticatedUserChange);
    };
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  // Set Zoho SalesIQ visitor info when user logs in/out
  useEffect(() => {
    const salesiq = window?.$zoho?.salesiq;
    if (salesiq?.visitor && user) {
      // Set visitor email and name for support identification
      salesiq.visitor.email?.(user.email);
      salesiq.visitor.name?.(user.name);
    } else if (salesiq?.reset && !user) {
      // Clear visitor info when user logs out
      salesiq.reset();
    }
  }, [user]);




  const login = async (email: string, password: string, mfaCode?: string, newPassword?: string) => {
    try {
      setError(null);
      setLoading(true);

      if (newPassword) {
        try {
          // Complete new password challenge
          const { isSignedIn } = await confirmSignIn({
            challengeResponse: newPassword
          });

          if (isSignedIn) {
            const authUser = await getCurrentUser();
            const userData = await mapAuthUserToUser(authUser);
            setUser(userData);
            setNewPasswordRequired(false);
            return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: false };
          }
        } catch {
          const message = 'Unable to set the new password. Check the password requirements and try again.';
          setError(message);
          setNewPasswordRequired(true);
          return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: true };
        }
      } else if (mfaCode) {
        try {
          // Complete MFA verification
          const { isSignedIn } = await confirmSignIn({
            challengeResponse: mfaCode
          });

          if (isSignedIn) {
            const authUser = await getCurrentUser();
            const userData = await mapAuthUserToUser(authUser);
            setUser(userData);
            setMfaRequired(false);
            return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: false };
          }
        } catch {
          const message = 'The verification code is invalid or expired. Please try again.';
          setError(message);
          setMfaRequired(true);
          return { requiresMfa: true, requiresEmailConfirmation: false, requiresNewPassword: false };
        }
      } else {
        // Initial login attempt
        const result = await signIn({
          username: email.toLowerCase(),
          password: password,
          options: {
            clientMetadata: {
              signInMethod: 'password',
            },
          },
        });


        // Admin-initiated password reset: user must go through confirmResetPassword
        // (verification code + new password). The admin reset already triggered code delivery.
        if (result.nextStep.signInStep === 'RESET_PASSWORD') {
          console.log('[AuthContext] Admin-initiated password reset detected');
          setPasswordResetRequired(true);
          setPasswordResetEmail(email.toLowerCase());
          return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: false, requiresPasswordReset: true };
        }
        // Check if new password is required
        else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setNewPasswordRequired(true);
          return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: true };
        }
        // Check if MFA is required
        else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setMfaRequired(true);
          return { requiresMfa: true, requiresEmailConfirmation: false, requiresNewPassword: false };
        } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          return { requiresEmailConfirmation: true, requiresMfa: false, requiresNewPassword: false };
        }

        // If no MFA required, proceed with normal login
        if (result.isSignedIn) {
          const authUser = await getCurrentUser();
          const userData = await mapAuthUserToUser(authUser);
          setUser(userData);
          return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: false };
        }
      }
    } catch (error) {
      setMfaRequired(false);
      setNewPasswordRequired(false);
      setPasswordResetRequired(false);
      setPasswordResetEmail('');

      // Detect admin-initiated password reset: Amplify v6 may throw PasswordResetRequiredException
      // as an error (or mask it) instead of returning RESET_PASSWORD step.
      // Check all possible shapes: name, message, code, __type, or anywhere in stringified error.
      const errName = error instanceof Error ? error.name : '';
      const errMsg = error instanceof Error ? error.message : String(error);
      const errCode = String((error as Record<string, unknown>)?.code || '');
      const errType = String((error as Record<string, unknown>)?.__type || '');
      const errStr = (errName + ' ' + errMsg + ' ' + errCode + ' ' + errType).toLowerCase();

      if (
        errName === 'PasswordResetRequiredException' ||
        errCode === 'PasswordResetRequiredException' ||
        errType.includes('PasswordResetRequired') ||
        errStr.includes('password reset required') ||
        errStr.includes('password reset is required') ||
        errStr.includes('passwordresetrequired')
      ) {
        setLoading(false);
        return { requiresMfa: false, requiresEmailConfirmation: false, requiresNewPassword: false, requiresPasswordReset: true };
      }

      if (error instanceof Error) {
        // Check if user is disabled
        if ('name' in error && error.name === 'NotAuthorizedException' &&
          error.message.includes('User is disabled')) {
          // Check if maintenance mode is enabled
          if (maintenanceConfig.enabled) {
            setError('MAINTENANCE_MODE');
          } else {
            setError('Your account has been temporarily disabled. Please contact support for assistance.');
          }
        } else if ('name' in error && error.name === 'UserNotConfirmedException') {
          setError('Please verify your email before signing in.');
        } else if ('name' in error && error.name === 'NotAuthorizedException') {
          const message = error.message || '';
          if (message.toLowerCase().includes('passkey')) {
            setError('Password sign-in is disabled for this account. Use your passkey instead.');
          } else {
            setError('Incorrect username or password.');
          }
        } else if ('name' in error && error.name === 'CodeMismatchException') {
          setError('Invalid verification code. Please try again.');
        } else {
          setError(error.message || 'Login failed');
        }
      } else {
        setError('An unknown error occurred during login');
      }

      // Only throw in development
      if (import.meta.env.VITE_APP_ENV === 'development') {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const loginWithPasskey = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      setMfaRequired(false);

      const result = await signIn({
        username: email.toLowerCase(),
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'WEB_AUTHN',
          clientMetadata: {
            signInMethod: 'passkey',
          },
        },
      });

      if (result.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        const available = result.nextStep.availableChallenges || [];
        if (!available.includes('WEB_AUTHN')) {
          // Cognito often omits WEB_AUTHN when authenticator-app (TOTP) MFA is preferred.
          // Let the login UI fall back to password instead of treating this as a hard failure.
          setError(null);
          return {
            requiresMfa: false,
            requiresEmailConfirmation: false,
            requiresNewPassword: false,
            passkeyAvailable: false,
          };
        }
        const confirmResult = await confirmSignIn({
          challengeResponse: 'WEB_AUTHN',
        });
        if (confirmResult.isSignedIn) {
          const authUser = await getCurrentUser();
          const userData = await mapAuthUserToUser(authUser);
          setUser(userData);
          return {
            requiresMfa: false,
            requiresEmailConfirmation: false,
            requiresNewPassword: false,
            success: true,
            passkeyAvailable: true,
          };
        }
        if (confirmResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          setMfaRequired(true);
          return {
            requiresMfa: true,
            requiresEmailConfirmation: false,
            requiresNewPassword: false,
            passkeyAvailable: true,
          };
        }
        // Amplify may complete the WebAuthn ceremony inside confirmSignIn and return DONE
        if (confirmResult.nextStep.signInStep === 'DONE' || confirmResult.isSignedIn) {
          const authUser = await getCurrentUser();
          const userData = await mapAuthUserToUser(authUser);
          setUser(userData);
          return {
            requiresMfa: false,
            requiresEmailConfirmation: false,
            requiresNewPassword: false,
            success: true,
            passkeyAvailable: true,
          };
        }
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setMfaRequired(true);
        return {
          requiresMfa: true,
          requiresEmailConfirmation: false,
          requiresNewPassword: false,
          passkeyAvailable: true,
        };
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        return {
          requiresEmailConfirmation: true,
          requiresMfa: false,
          requiresNewPassword: false,
          passkeyAvailable: false,
        };
      }

      if (result.isSignedIn || result.nextStep.signInStep === 'DONE') {
        const authUser = await getCurrentUser();
        const userData = await mapAuthUserToUser(authUser);
        setUser(userData);
        return {
          requiresMfa: false,
          requiresEmailConfirmation: false,
          requiresNewPassword: false,
          success: true,
          passkeyAvailable: true,
        };
      }

      setError('Passkey sign-in could not be completed.');
      return {
        requiresMfa: false,
        requiresEmailConfirmation: false,
        requiresNewPassword: false,
        passkeyAvailable: true,
      };
    } catch (error) {
      const errName = error instanceof Error ? error.name : '';
      const errMsg = error instanceof Error ? error.message : String(error);
      const errStr = `${errName} ${errMsg}`.toLowerCase();

      if (
        errStr.includes('cancel') ||
        errStr.includes('abort') ||
        errName === 'NotAllowedError' ||
        errName === 'PasskeyError'
      ) {
        setError('Passkey sign-in was cancelled or failed.');
        return {
          requiresMfa: false,
          requiresEmailConfirmation: false,
          requiresNewPassword: false,
          cancelled: true,
          passkeyAvailable: true,
        };
      } else if (
        errStr.includes('web_authn') ||
        errStr.includes('passkey') ||
        errStr.includes('preferred challenge') ||
        errStr.includes('not available')
      ) {
        setError('Passkey sign-in could not be completed. Try again or use another sign-in method.');
        return {
          requiresMfa: false,
          requiresEmailConfirmation: false,
          requiresNewPassword: false,
          passkeyAvailable: false,
        };
      } else if (errStr.includes('user does not exist') || errName === 'UserNotFoundException') {
        setError('Passkey sign-in could not be completed. Try again or use another sign-in method.');
        return {
          requiresMfa: false,
          requiresEmailConfirmation: false,
          requiresNewPassword: false,
          passkeyAvailable: false,
        };
      } else if (error instanceof Error) {
        setError('Passkey sign-in could not be completed. Try again or use another sign-in method.');
      } else {
        setError('Passkey sign-in could not be completed. Try again or use another sign-in method.');
      }

      if (import.meta.env.VITE_APP_ENV === 'development') {
        throw error;
      }

      return {
        requiresMfa: false,
        requiresEmailConfirmation: false,
        requiresNewPassword: false,
        passkeyAvailable: false,
      };
    } finally {
      setLoading(false);
    }
  };

  const loginWithSSO = async (organizationId: string) => {
    try {
      setLoading(true);
      setError(null);
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({
        provider: { idpIdentifier: organizationId } // Passes 'idp_identifier' matching the WorkOS Org ID
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'An error occurred during Single Sign-On.');
      } else {
        setError('An error occurred during Single Sign-On.');
      }
      setLoading(false);
    }
  };

  const loginWithMicrosoft = async (providerName?: string) => {
    try {
      setLoading(true);
      setError(null);
      // Use the explicitly passed provider name (for dynamic tenant-specific IdPs),
      // or fall back to the environment-configured default (Simply Invent Labs' own IdP).
      const idpName = providerName || activePortalSettings.microsoftIdpName || 'MicrosoftEntraSSOForSimplySend';
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({
        provider: { custom: idpName },
        options: {
          // Cognito can otherwise reuse its own browser session without
          // redirecting to Microsoft. Reauthentication forces that redirect
          // and lets the user choose another Microsoft account.
          prompt: 'LOGIN',
        },
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || 'An error occurred during Microsoft sign-in.');
      } else {
        setError('An error occurred during Microsoft sign-in.');
      }
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({
        provider: 'Google',
        options: {
          // Cognito can otherwise reuse its own browser session without
          // redirecting to Google. Reauthentication forces that redirect
          // and lets the user choose another Google account.
          prompt: 'LOGIN',
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during Google sign-in.');
      setLoading(false);
    }
  };

  const loginWithSocialProvider = async (providerName: string, providerLabel: string) => {
    try {
      setLoading(true);
      setError(null);
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({
        provider: { custom: providerName },
        options: {
          // Ensure Cognito redirects to the provider and lets the user choose
          // a different account instead of silently reusing its session.
          prompt: 'LOGIN',
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : `An error occurred during ${providerLabel} sign-in.`);
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string, referralCode?: string): Promise<boolean> => {
    try {
      setError(null);

      const nameParts = name.trim().split(/\s+/);
      const userAttributes: Record<string, string> = {
        email: email.toLowerCase(),
      };

      if (nameParts.length >= 2) {
        const lastName = nameParts[nameParts.length - 1];
        const firstName = nameParts.slice(0, -1).join(' ');
        userAttributes.given_name = firstName;
        userAttributes.family_name = lastName;
      } else {
        userAttributes.given_name = nameParts[0];
      }

      // Only add referralCode if it exists and is not empty
      if (referralCode && referralCode.trim() !== '') {
        userAttributes['custom:referralCode'] = referralCode;
      }

      await signUp({
        username: email.toLowerCase(),
        password: password,
        options: {
          userAttributes,
        },
      });
      return true;
    } catch (error: unknown) {
      // Extract error message reliably
      let rawMessage = '';
      if (typeof error === 'string') {
        rawMessage = error;
      } else if (error && typeof error === 'object') {
        const errObj = error as Record<string, unknown>;
        rawMessage = String(errObj.message || errObj.name || JSON.stringify(error));
      } else {
        rawMessage = 'Signup failed';
      }

      let errorMessage = rawMessage;

      // Clean up Cognito's internal error prefixes
      if (errorMessage.startsWith('PreSignUp failed with error ')) {
        errorMessage = errorMessage.replace('PreSignUp failed with error ', '');
      }

      // Handle disabled sign up specifically (case-insensitive check)
      if (rawMessage.toLowerCase().includes('signup is not permitted')) {
        errorMessage = 'Registration is currently disabled. Please check the website homepage (www.simplysend.email) for more information.';
      }

      setError(errorMessage);

      // Only throw in development for non-disabled-signup errors
      if (import.meta.env.VITE_APP_ENV === 'development' && !rawMessage.toLowerCase().includes('signup is not permitted')) {
        throw error;
      }
      return false;
    }
  };

  const confirmSignup = async (email: string, code: string): Promise<{ userId: string }> => {
    try {
      const result = await confirmSignUp({
        username: email.toLowerCase(),
        confirmationCode: code,
      });
      // Amplify v6 returns { isSignUpComplete, nextStep } — no userId field
      return { userId: result.isSignUpComplete ? 'confirmed' : '' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Confirmation failed';
      console.error('confirmSignup error:', errorMessage);
      return { userId: '' };
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      setError(null);
      setLoading(true);

      const { resetPassword } = await import('aws-amplify/auth');
      await resetPassword({ username: email });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset code';
      setError(errorMessage);

      // Only throw in development
      if (import.meta.env.VITE_APP_ENV === 'development') {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string, code: string, newPassword: string) => {
    try {
      setError(null);
      setLoading(true);

      const { confirmResetPassword } = await import('aws-amplify/auth');
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      setError(errorMessage);

      // Only throw in development
      if (import.meta.env.VITE_APP_ENV === 'development') {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };
  const logout = async () => {
    try {
      // Best-effort activity log — must not delay or block sign-out.
      await apiService.logout().catch(() => undefined);
      await signOut({ global: true });
    } catch {
      // Local credentials and application state are still cleared below.
    } finally {
      apiService.setWorkspaceId(null);
      apiService.setAuthenticatedUser(null);
      queryClient.clear();
    }
    // Hard-navigate immediately. Do not clear React user state first — that
    // would briefly mount Login on the current deep link, which defaults to
    // the sign-up view before /sign-in loads.
    window.location.replace('/sign-in');
  };

  const clearError = () => {
    setError(null);
  };

  const resetMfa = () => {
    setMfaRequired(false);
    setNewPasswordRequired(false);
    setPasswordResetRequired(false);
    setPasswordResetEmail('');
    setError(null);
  };

  const switchWorkspace = async (workspaceId: string) => {
    // Serialize switches so an earlier permission refresh cannot overwrite a later selection.
    while (workspaceSwitchRef.current) await workspaceSwitchRef.current;
    if (!user) throw new Error('Sign in before switching workspaces');
    const previousWorkspaceId = apiService.getWorkspaceId();
    if (workspaceId === previousWorkspaceId) return;

    // Unmount workspace consumers before changing request headers or clearing their queries.
    flushSync(() => setSwitchingWorkspace(true));
    const pendingSwitch = (async () => {
      try {
        await queryClient.cancelQueries();
        queryClient.clear();
        // Newly created workspaces may not yet be in the cached switcher list.
        if (!user.workspaces.some(workspace => workspace.workspaceId === workspaceId)) {
          const { workspaces } = await apiService.getTeams();
          if (!workspaces.some(workspace => workspace.workspaceId === workspaceId)) {
            throw new Error('Workspace is not available to the current user');
          }
        }
        apiService.setWorkspaceId(workspaceId);
        const authUser = await getCurrentUser();
        const userData = await mapAuthUserToUser(authUser, false);
        setUser(userData);
      } catch (switchError) {
        apiService.setWorkspaceId(previousWorkspaceId);
        queryClient.clear();
        throw switchError;
      } finally {
        setSwitchingWorkspace(false);
      }
    })();
    workspaceSwitchRef.current = pendingSwitch;
    try {
      await pendingSwitch;
    } finally {
      if (workspaceSwitchRef.current === pendingSwitch) workspaceSwitchRef.current = null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      loginWithPasskey,
      loginWithSSO,
      loginWithMicrosoft,
      loginWithGoogle,
      loginWithSocialProvider,
      signup,
      confirmSignup,
      forgotPassword,
      resetPassword,
      logout,
      loading,
      authReady,
      error,
      mfaRequired,
      newPasswordRequired,
      passwordResetRequired,
      passwordResetEmail,
      setMfaRequired,
      setNewPasswordRequired,
      setPasswordResetRequired,
      clearError,
      resetMfa,
      refreshUser: checkAuthState,
      switchWorkspace
    }}>
      {switchingWorkspace ? (
        <div role="status" aria-busy="true" className="flex min-h-screen items-center justify-center gap-3 bg-white text-zinc-700 dark:bg-zinc-950 dark:text-zinc-200">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-cyan-500"></div>
          <span>Switching workspace...</span>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
